import styled from "@emotion/styled";
import { useState, useEffect } from "react";
import {
  Typography,
  Container,
  IconButton,
  Paper,
  Box,
  Button,
  Divider,
  ListItem
} from "@mui/material";
import Grid from "@mui/material/Grid";
import { Resizable } from "re-resizable";

import {
  Home,
  HomeOutlined,
  Message,
  MessageOutlined,
  UnfoldLess,
  UnfoldMore
} from "@mui/icons-material";

import { useTranslation } from "react-i18next";

import useAuth from "../../hooks/useAuth";

import {
  ChatType,
  IThread,
  NewChatBoxMessage,
  TabType
} from "../../types/chatBot";
import ChatHome from "../../components/botChat/ChatHome";
import AssistantThreads from "../../components/botChat/AssistantThreads";
import ChatBox from "../../components/botChat/AssistantThreadBox";
import GoBeyondLogo from "../../icons/GoBeyondLogo";
import { ChevronLeft } from "react-feather";
import { useGetOrganizationBrandingQuery } from "../../redux/slices/graphApiSlice";
import useAppSelector from "../../hooks/useAppSelector";
import useBotChat from "../../hooks/useBotChat";

const Content = styled(Box)`
  background: ${(props) => props.theme.chatbot.color};
  background: ${(props) => props.theme.chatbot.background};

  transform-origin: right top;
  box-shadow: rgba(0, 0, 0, 0.16) 0px 5px 20px;
  border-radius: 16px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  transform: scale(0);
  opacity: 0;
  transition: all 0.3s;

  &.show {
    opacity: 1;
    transform: scale(1);
  }
`;

const Header = styled(ListItem)<{}>`
  min-height: 50px;
  color: ${(props) => props.theme.sidebar.header.text};
  font-family: ${(props) => props.theme.typography.fontFamily};
  padding-left: ${(props) => props.theme.spacing(6)};
  padding-right: ${(props) => props.theme.spacing(6)};
  padding-top: ${(props) => props.theme.spacing(4)};
  padding-bottom: ${(props) => props.theme.spacing(4)};
  flex-grow: 0;
`;

const NavIcon = styled(Button)<{ state: "active" | "normal" }>`
  display: flex;
  flex-direction: column;
  color: ${(props) => props.theme.palette.primary.main};
  font-weight: ${(props) => (props.state === "active" ? "bolder" : "normal")};

  &:hover {
    color: ${(props) => props.theme.palette.primary.main};
    background-color: transparent;
  }
  svg {
    width: 30px;
    height: 30px;
  }
`;

const HeaderButton = styled(IconButton)`
  display: flex;
  background: transparent;
  border: none;
  align-items: center;
  color: ${(props) => props.theme.sidebar.color};
  cursor: pointer;
`;

const getStoredDimensions = () => {
  const storedDimensions = sessionStorage.getItem("chatbot-dimensions");
  if (storedDimensions) {
    try {
      const parsed = JSON.parse(storedDimensions);
      return {
        width: parsed.width || 400,
        height: parsed.height || 600
      };
    } catch (error) {
      console.warn("Failed to parse stored dimensions:", error);
    }
  }
  return {
    width: 400,
    height: 600
  };
};
function AskMeAnythingChatBotContent() {
  const { addedMessage, open: showChat } = useBotChat();
  const [tab, setTab] = useState<TabType>(TabType.chatBox);
  const [thread, setThread] = useState<IThread>();
  const [expanded, setExpanded] = useState<boolean | undefined>();
  const [newMessage, setNewMessage] = useState<NewChatBoxMessage | null>(null);
  const [dimensions, setDimensions] = useState(() => {
    const storedDimensions = getStoredDimensions();
    return storedDimensions;
  });

  useEffect(() => {
    if (addedMessage) {
      setTab(TabType.chatBox);
      setNewMessage({
        type: ChatType.GENERAL,
        message: addedMessage
      });
    }
  }, [addedMessage]);

  useEffect(() => {
    if (expanded) {
      setDimensions({
        width: window.innerWidth - 100,
        height: window.innerHeight - 200
      });
    } else {
      setDimensions(() => {
        const storedDimensions = getStoredDimensions();
        return storedDimensions;
      });
    }
  }, [expanded]);

  const handleSelectChatBox = (selected: IThread) => {
    setNewMessage(null);
    setThread(selected);
    setTab(TabType.chatBox);
  };

  const handleBackToHistory = () => {
    setTab(TabType.history);
  };

  const handleCreateChatBox = () => {
    setNewMessage(null);
    setThread(undefined);
    setTab(TabType.chatBox);
  };

  const handleResize = (event: any, direction: any, ref: any, delta: any) => {
    const newDimensions = {
      width: parseInt(ref.style.width),
      height: parseInt(ref.style.height)
    };
    setDimensions(newDimensions);
    sessionStorage.setItem("chatbot-dimensions", JSON.stringify(newDimensions));
    setExpanded(false);
  };

  useEffect(() => {
    const handleWindowResize = () => {
      const maxWidth = window.innerWidth - 100;
      const maxHeight = window.innerHeight - 200;

      if (dimensions.width > maxWidth || dimensions.height > maxHeight) {
        const adjustedDimensions = {
          width: Math.min(dimensions.width, maxWidth),
          height: Math.min(dimensions.height, maxHeight)
        };
        setDimensions(adjustedDimensions);
        sessionStorage.setItem(
          "chatbot-dimensions",
          JSON.stringify(adjustedDimensions)
        );
      }
    };

    window.addEventListener("resize", handleWindowResize);
    return () => window.removeEventListener("resize", handleWindowResize);
  }, [dimensions]);

  const { t } = useTranslation();
  const tenantId = useAppSelector((state) => state.auth.tenantId);

  const { data: organizationBranding } = useGetOrganizationBrandingQuery(
    tenantId || "",
    {
      skip: !tenantId
    }
  );

  const { user } = useAuth();

  return (
    <Resizable
      size={dimensions}
      onResize={handleResize}
      minWidth={300}
      minHeight={400}
      maxWidth={window.innerWidth - 100}
      maxHeight={window.innerHeight - 200}
      enable={{
        top: true,
        right: false,
        bottom: false,
        left: true,
        topRight: false,
        bottomRight: false,
        bottomLeft: false,
        topLeft: true
      }}
    >
      <Paper
        sx={{
          backgroundColor: "transparent",
          boxShadow: "none",
          height: "100%",
          width: "100%"
        }}
      >
        <Content
          className={showChat ? "show" : ""}
          flexDirection="column"
          display="flex"
          height="100%"
        >
          <Header
            sx={{
              justifyContent: tab === TabType.history ? "center" : "initial"
            }}
          >
            {tab === TabType.home && (
              <Box display="flex" justifyContent="center">
                {organizationBranding ? (
                  <img
                    src={
                      user?.account?.logo
                        ? user?.account?.logo
                        : organizationBranding?.bannerLogoRelativeUrl
                    }
                    alt="logo"
                    height={60}
                    style={{
                      marginRight: 8,
                      paddingTop: 6,
                      maxWidth: 280,
                      objectFit: "contain"
                    }}
                  />
                ) : (
                  <GoBeyondLogo showText={true} />
                )}
              </Box>
            )}

            {tab === TabType.history && (
              <strong className="no-cursor">
                <Typography variant="h5" display="flex" justifyContent="center">
                  {t("Conversations")}
                </Typography>
              </strong>
            )}

            {tab === TabType.chatBox && (
              <>
                <strong className="no-cursor">
                  <HeaderButton onClick={handleBackToHistory}>
                    <ChevronLeft />
                  </HeaderButton>
                </strong>
                <Container component={"div"} />
                <strong className="no-cursor">
                  <HeaderButton onClick={() => setExpanded(!expanded)}>
                    {expanded ? <UnfoldLess /> : <UnfoldMore />}
                  </HeaderButton>
                </strong>
              </>
            )}
          </Header>
          <Box sx={{ flex: 1, overflowY: "auto" }}>
            {tab === TabType.home && (
              <ChatHome
                onConversationClick={handleSelectChatBox}
                onCreateChatBox={handleCreateChatBox}
                setTab={setTab}
              />
            )}
            {tab === TabType.history && (
              <AssistantThreads
                onConversationClick={handleSelectChatBox}
                onCreateChatBox={handleCreateChatBox}
                refetch={handleBackToHistory}
              />
            )}
            {tab === TabType.chatBox && (
              <ChatBox selectedThread={thread} newMessage={newMessage} />
            )}
          </Box>
          {tab !== TabType.chatBox && (
            <Box>
              <Divider variant="inset" sx={{ marginLeft: 0 }} />
              <Grid
                container
                justifyContent="space-around"
                alignItems="center"
                height={80}
              >
                <Grid>
                  <NavIcon
                    state={tab === TabType.home ? "active" : "normal"}
                    variant="text"
                    onClick={() => setTab(TabType.home)}
                    sx={{ transition: "all 0.3s" }}
                  >
                    {tab === TabType.home ? (
                      <Home color="primary" />
                    ) : (
                      <HomeOutlined />
                    )}
                    {t("Home")}
                  </NavIcon>
                </Grid>
                <Grid>
                  <NavIcon
                    state={tab === TabType.history ? "active" : "normal"}
                    variant="text"
                    onClick={() => setTab(TabType.history)}
                    sx={{ transition: "all 0.3s" }}
                  >
                    {tab === TabType.history ? (
                      <Message color="primary" />
                    ) : (
                      <MessageOutlined />
                    )}
                    {t("Conversations")}
                  </NavIcon>
                </Grid>
              </Grid>
            </Box>
          )}
        </Content>
      </Paper>
    </Resizable>
  );
}

export default AskMeAnythingChatBotContent;
